<script setup lang="ts">
  import { Modal } from 'bootstrap'

  import { ref, onMounted, computed, watch } from 'vue'

  import Icon from '@/components/Atoms/Icon.vue'
  import { useModalControl } from '../store/pinia/useModalControl.store'
  import usePluginCertificatePasswordTipModal from '../composables/usePluginCertificatePasswordTipModal'
  import ModalPluginCertificatePasswordTip from './ModalPluginCertificatePasswordTip.vue'

  const emit = defineEmits(['chosePasswordConfirm'])

  const modal = ref<InstanceType<typeof Modal> | null>(null)

  const { pluginCertificatePasswordTipModal } =
    usePluginCertificatePasswordTipModal()

  const useDifferentPassword = ref<boolean>(false)

  const useSamePassword = ref<boolean>(false)

  const hasOptionsSelected = computed(
    () => useDifferentPassword.value || useSamePassword.value,
  )

  onMounted(() => {
    modal.value = new Modal('#modal-plugin-certificate-password', {
      backdrop: 'static',
      keyboard: false,
    })
  })

  function hide() {
    modal.value?.hide()
  }

  function show() {
    useSamePassword.value = false
    useDifferentPassword.value = false
    modal.value?.show()
  }

  watch(useDifferentPassword, (value) => {
    if (value) {
      useSamePassword.value = false
    }
  })

  watch(useSamePassword, (value) => {
    if (value) {
      useDifferentPassword.value = false
    }
  })

  async function advanceNextStep(): Promise<void> {
    const modalControlStore = useModalControl()
    const {
      hidePluginCertificatePasswordTip,
      showPluginCertificatePasswordTip,
    } = usePluginCertificatePasswordTipModal()

    hide()

    return modalControlStore.show({
      modalId: 'pluginCertificatePasswordTipModal',
      hide: hidePluginCertificatePasswordTip,
      show: showPluginCertificatePasswordTip,
    })
  }

  function resendEmit() {
    emit('chosePasswordConfirm')
  }

  defineExpose({ hide, show })
</script>

<template>
  <div
    id="modal-plugin-certificate-password"
    class="modal modal-md fade"
    tabindex="-1"
    data-bs-backdrop="static"
    data-bs-keyboard="false"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header pb-3 border-bottom">
          <h5 class="modal-title plugin-modal-title">Falta pouco!</h5>
          <button class="btn-close" @click="hide" />
        </div>
        <div class="modal-body py-3">
          <img
            class="d-block mx-auto img-container"
            src="@/assets/images/Sala_de_espera.svg"
            alt="Menina com xícara de café com um desenho de coração"
          />

          <p class="fs-6 text-start mt-4">
            <strong>
              Para utilizar seu Certificado Digital, o que você prefere?
            </strong>
          </p>

          <div class="container user-select-none">
            <div class="row">
              <div
                class="form-check d-flex w-100"
                @click="() => (useDifferentPassword = !useDifferentPassword)"
                @keypress="() => null"
              >
                <input
                  type="radio"
                  class="form-check-input form-check-input--success square-circle-container"
                  :checked="!useSamePassword && useDifferentPassword"
                />
                <label class="form-check-label ms-3 label-text-left">
                  Criar uma senha para uso do Certificado.
                </label>
              </div>
              <div
                class="form-check d-flex"
                @click="() => (useSamePassword = !useSamePassword)"
                @keypress="() => null"
              >
                <input
                  type="radio"
                  class="form-check-input form-check-input--success square-circle-container"
                  :checked="!useDifferentPassword && useSamePassword"
                />
                <label class="form-check-label ms-3 label-text-left">
                  Manter a senha de emissão para o uso do Certificado.
                </label>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer py-3">
          <button
            class="btn"
            :class="[!hasOptionsSelected ? 'btn-secondary' : 'btn-primary']"
            type="button"
            :disabled="!hasOptionsSelected"
            @click="advanceNextStep"
          >
            <span>Avançar</span>
            <Icon class="ms-2" name="arrow-right"></Icon>
          </button>
        </div>
      </div>
    </div>
  </div>

  <modal-plugin-certificate-password-tip
    ref="pluginCertificatePasswordTipModal"
    :is-different-pass="useDifferentPassword"
    @chosePasswordConfirm="resendEmit"
  />
</template>

<style scoped>
  .plugin-modal-title {
    font-size: 1.5rem;
  }

  .label-text-left {
    text-align: left !important;
  }

  .square-circle-container {
    min-width: 16px;
    min-height: 16px;
  }

  .img-container {
    width: 180px;
    height: auto;
  }
</style>
